/* global affirm */
import { useState } from 'react';
import { get } from 'lodash';
import { format } from 'date-fns';
import { gql, useMutation } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import { UPDATE_ORDER } from 'mocks/mutations';
import useAffirmScript from 'hooks/useAffirmScript';
import useLogoutClient from 'data-hooks/useUserData/useLogoutClient';

const GET_CUSTOMER_PROTECTION_PLANS = gql`
  query getProtectionPlansList($page: Int, $limit: Int) {
    protectionPlans: getProtectionPlansList(page: $page, limit: $limit) {
      errors {
        code
        message
      }
      items {
        name
        cart_id
        product_id
        quantity
        small_image
        variants_text
        variants_sku
        imageURL
        pickLater
        options {
          id
          name
          value
          image
          optionTypeId
          optionTypeName
          default_value
        }
        applied_protection_plan {
          plan_id
          plan_name
          plan_price
        }
        available_protection_plans {
          plan_id
          plan_name
          plan_price
          plan_description
        }
        is_clearance_item
        order_id
        dated
        order_status
        order_type
        shipment_status
        total_order_amount
      }
    }
  }
`;

const LIMIT = 10;

const useProtectionPlans = ({ skip = false } = {}) => {
  // load affirm if not loaded
  useAffirmScript();

  // state
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoadMore, setHasLoadMore] = useState(true);
  const [errors, setErrors] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [eligiblePlans, setEligiblePlans] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // queries
  const { loading, error } = useSuspenseQuery(GET_CUSTOMER_PROTECTION_PLANS, {
    variables: {
      page,
      limit: LIMIT,
    },
    skip,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const { items, errors: err } = data?.protectionPlans || {};
      if (err) {
        setErrors(err);
      }

      if (items?.length) {
        let plans = [];
        const list = [];
        items.forEach(item => {
          if (
            item &&
            item.available_protection_plans &&
            item.available_protection_plans.length
          ) {
            if (!plans.length && item.available_protection_plans.length === 2) {
              plans = item.available_protection_plans.concat([]);
            }

            let orderDate;
            try {
              if (item.dated) {
                orderDate = format(
                  new Date(item.dated.split(' ')[0]),
                  'MM/dd/yyyy'
                );
              }
            } catch (e) {
              // ignore error
            }
            list.push({
              order_id: item.order_id,
              order_status: item.order_status,
              dated: orderDate,
              name: item.name,
              cart_id: item.cart_id,
              product_id: item.product_id,
              images: item.images,
              imageURL: item.imageURL,
              options: item.options,
              sku: item.variants_sku,
              quantity: item.quantity,
              small_image: item.small_image,
              variants_text: item.variants_text,
              applied_protection_plan: item.applied_protection_plan,
              available_protection_plans:
                item.available_protection_plans &&
                item.available_protection_plans.length
                  ? [...item.available_protection_plans].sort(
                      (a, b) => b.plan_price - a.plan_price
                    )
                  : [],
              is_clearance_item: item.is_clearance_item,
            });
          }
        });

        setCartItems([...cartItems, ...list]);
        if (!eligiblePlans.length) {
          setEligiblePlans(plans);
        }
      }

      if (items?.length < LIMIT) {
        setHasLoadMore(false);
      }

      setLoadingMore(false);
    },
  });

  // mutations
  const [updateOrderMutation] = useMutation(UPDATE_ORDER);
  const logoutClient = useLogoutClient();

  // functions
  const refetch = () => {
    setHasLoadMore(true);
    setCartItems([]);
    setPage(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPage(page + 1);
  };

  const purchaseProtectionPlan = async ({
    orderId,
    cartItem,
    orderInfo,
    taxTotal,
    grandTotal,
    newCCMethod,
    affirmMethod,
    selectedPlan,
    payPalMethod,
    applePayMethod,
    pointOfSaleMethod,
    existingCCMethod,
  }) => {
    setErrorMessage(null);
    try {
      let payment_token;
      if (affirmMethod) {
        if (orderInfo) {
          const affirmPayment = orderInfo.payment?.find(
            p => p.payment_type === 'Affirm'
          );
          payment_token = affirmPayment?.payment_token;

          if (!payment_token) {
            setErrorMessage('Payment token not found');
            return;
          }
        }
      }

      const plan_id = selectedPlan.plan_id?.toString();
      let res = await updateOrderMutation({
        variables: {
          input: {
            sku: plan_id,
            order_id: orderId,
            cart_id: cartItem.cart_id,
            plan_id,
            taxTotal,
            grandTotal,
            newCCMethod,
            affirmMethod,
            payPalMethod,
            applePayMethod,
            pointOfSaleMethod,
            existingCCMethod,
          },
        },
      });

      res = get(res, 'data.updateOrder');
      const success = get(res, 'success', false);

      if (success) {
        // affirm method
        if (affirmMethod) {
          if (orderInfo) {
            const { shipping, billing, customer } = orderInfo;

            const { location } = window;
            const baseUrl = `${location.origin}${location.pathname}`;
            const checkoutInfo = {
              merchant: {
                user_confirmation_url: `${baseUrl}?oid=${orderId}&cart_id=${cartItem.cart_id}&cid=${customer.customer_id}&email=${customer.customer_email}&plan_id=${selectedPlan.plan_id}&plan_name=${selectedPlan.plan_name}&product=${cartItem.name}&payment_token=${payment_token}`,
                user_cancel_url: baseUrl,
                user_confirmation_url_action: 'GET',
                name: 'Joybird',
              },
              shipping: {
                name: {
                  first: customer.customer_first_name,
                  last: customer.customer_last_name,
                },
                address: {
                  line1: shipping.address_street1,
                  line2: shipping.address_street2 || '',
                  city: shipping.address_city,
                  state: shipping.address_state,
                  country: shipping.address_country,
                  zipcode: shipping.address_zip_code,
                },
                phone_number: shipping.address_phone,
                email: customer.customer_email,
              },
              billing: {
                name: {
                  first: billing.address_first_name,
                  last: billing.address_last_name,
                },
                address: {
                  line1: billing.address_street1,
                  line2: billing.address_street2 || '',
                  city: billing.address_city,
                  state: billing.address_state,
                  country: billing.address_country,
                  zipcode: billing.address_zip_code,
                },
                phone_number: billing.address_phone,
                email: customer.customer_email,
              },
              items: [
                {
                  display_name: selectedPlan.plan_name,
                  sku: selectedPlan.plan_id,
                  unit_price: Math.round((grandTotal - taxTotal) * 100),
                  qty: cartItem.quantity,
                },
              ],

              metadata: {
                platform_type: '4.0',
                mode: 'modal',
              },

              shipping_amount: 0,
              tax_amount: Math.round(taxTotal * 100),
              total: Math.round(grandTotal * 100),
            };

            affirm.checkout(checkoutInfo);
            affirm.checkout.open();
          }
        } else {
          setIsSuccess(true);
          refetch();
        }
      } else {
        const message = get(res, 'errors.0.message', null);
        if (message) setErrorMessage(message);
      }
    } catch (e) {
      setErrorMessage(e.message);
    }
  };

  return {
    data: {
      loading: !loadingMore && loading,
      loadingMore,
      cartItems,
      hasLoadMore,
      eligiblePlans,
      error: errors || error,
      isUpdateSuccess: isSuccess,
      purchaseErrorMessage: errorMessage,
    },
    actions: {
      logout: logoutClient,
      loadMore,
      purchaseProtectionPlan,
      setIsUpdateSuccess: setIsSuccess,
      setPurchaseErrorMessage: setErrorMessage,
    },
  };
};

export default useProtectionPlans;
